import { useState, useCallback, useEffect } from 'react'
import type { Wallet, WalletTransaction } from '../types'
import { MOCK_WALLET_TRANSACTIONS } from '../constants/data'

const STORAGE_KEY = 'aerozeep_wallet'

// Calculate initial balance from transactions
const calculateBalance = (transactions: WalletTransaction[]): number => {
  return transactions.reduce((balance, txn) => {
    if (txn.status !== 'completed') return balance
    if (txn.type === 'withdrawal') return balance // Withdrawals don't affect balance until completed
    return txn.type === 'credit' || txn.type === 'promo'
      ? balance + txn.amount
      : balance - txn.amount
  }, 0)
}

const initialWallet: Wallet = {
  id: 'wallet-001',
  userId: 'user-001',
  balance: calculateBalance(MOCK_WALLET_TRANSACTIONS),
  transactions: MOCK_WALLET_TRANSACTIONS,
  createdAt: '2024-01-01T00:00:00',
  updatedAt: new Date().toISOString(),
}

export function useWallet() {
  const [wallet, setWallet] = useState<Wallet>(initialWallet)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load wallet from local storage or use initial mock data
  const loadWallet = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        setWallet(JSON.parse(stored))
      } else {
        // Initialize storage with mock data
        localStorage.setItem(STORAGE_KEY, JSON.stringify(initialWallet))
        setWallet(initialWallet)
      }
    } catch (err) {
      console.error('Failed to load wallet', err)
      setWallet(initialWallet)
    }
  }, [])

  // Save wallet to local storage
  const saveWallet = useCallback((newWallet: Wallet) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newWallet))
      setWallet(newWallet)
      // Dispatch a custom event so other components can update
      window.dispatchEvent(new Event('wallet-updated'))
    } catch (err) {
      console.error('Failed to save wallet', err)
    }
  }, [])

  // Fetch wallet data (mock)
  const fetchWallet = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))
      loadWallet()
    } catch {
      setError('Failed to load wallet data')
    } finally {
      setIsLoading(false)
    }
  }, [loadWallet])

  // Add credit to wallet (from refund)
  const addCredit = useCallback(
    async (
      amount: number,
      description: string,
      metadata?: WalletTransaction['metadata'],
    ): Promise<WalletTransaction> => {
      setIsLoading(true)
      setError(null)

      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000))

        const newTransaction: WalletTransaction = {
          id: `txn-${Date.now()}`,
          type: 'credit',
          amount,
          description,
          reference: `REF-${Date.now()}`,
          status: 'completed',
          createdAt: new Date().toISOString(),
          metadata,
        }

        const currentWallet = JSON.parse(
          localStorage.getItem(STORAGE_KEY) || JSON.stringify(initialWallet),
        )

        const newWallet = {
          ...currentWallet,
          balance: currentWallet.balance + amount,
          transactions: [newTransaction, ...currentWallet.transactions],
          updatedAt: new Date().toISOString(),
        }

        saveWallet(newWallet)
        return newTransaction
      } catch {
        setError('Failed to add credit')
        throw new Error('Failed to add credit')
      } finally {
        setIsLoading(false)
      }
    },
    [saveWallet],
  )

  // Debit from wallet (for booking)
  const debit = useCallback(
    async (
      amount: number,
      description: string,
      metadata?: WalletTransaction['metadata'],
    ): Promise<WalletTransaction> => {
      setIsLoading(true)
      setError(null)

      const currentWallet = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || JSON.stringify(initialWallet),
      )

      if (currentWallet.balance < amount) {
        setError('Insufficient balance')
        setIsLoading(false)
        throw new Error('Insufficient balance')
      }

      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000))

        const newTransaction: WalletTransaction = {
          id: `txn-${Date.now()}`,
          type: 'debit',
          amount,
          description,
          reference: `BKG-${Date.now()}`,
          status: 'completed',
          createdAt: new Date().toISOString(),
          metadata,
        }

        const newWallet = {
          ...currentWallet,
          balance: currentWallet.balance - amount,
          transactions: [newTransaction, ...currentWallet.transactions],
          updatedAt: new Date().toISOString(),
        }

        saveWallet(newWallet)
        return newTransaction
      } catch {
        setError('Failed to process payment')
        throw new Error('Failed to process payment')
      } finally {
        setIsLoading(false)
      }
    },
    [saveWallet],
  )

  // Apply promo code credit
  const applyPromoCredit = useCallback(
    async (amount: number, promoCode: string): Promise<WalletTransaction> => {
      setIsLoading(true)
      setError(null)

      try {
        await new Promise((resolve) => setTimeout(resolve, 1000))

        const newTransaction: WalletTransaction = {
          id: `txn-${Date.now()}`,
          type: 'promo',
          amount,
          description: `Promo Code Applied: ${promoCode}`,
          reference: `PROMO-${Date.now()}`,
          status: 'completed',
          createdAt: new Date().toISOString(),
          metadata: { promoCode },
        }

        const currentWallet = JSON.parse(
          localStorage.getItem(STORAGE_KEY) || JSON.stringify(initialWallet),
        )

        const newWallet = {
          ...currentWallet,
          balance: currentWallet.balance + amount,
          transactions: [newTransaction, ...currentWallet.transactions],
          updatedAt: new Date().toISOString(),
        }

        saveWallet(newWallet)
        return newTransaction
      } catch {
        setError('Failed to apply promo credit')
        throw new Error('Failed to apply promo credit')
      } finally {
        setIsLoading(false)
      }
    },
    [saveWallet],
  )

  // Process withdrawal (deduct from balance)
  const processWithdrawal = useCallback(
    async (
      amount: number,
      withdrawalId: string,
    ): Promise<WalletTransaction> => {
      setIsLoading(true)
      setError(null)

      const currentWallet = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || JSON.stringify(initialWallet),
      )

      if (currentWallet.balance < amount) {
        setError('Insufficient balance')
        setIsLoading(false)
        throw new Error('Insufficient balance')
      }

      try {
        await new Promise((resolve) => setTimeout(resolve, 1000))

        const newTransaction: WalletTransaction = {
          id: `txn-${Date.now()}`,
          type: 'withdrawal',
          amount,
          description: 'Withdrawal to Bank Account',
          reference: withdrawalId,
          status: 'completed',
          createdAt: new Date().toISOString(),
          metadata: { withdrawalId },
        }

        const newWallet = {
          ...currentWallet,
          balance: currentWallet.balance - amount,
          transactions: [newTransaction, ...currentWallet.transactions],
          updatedAt: new Date().toISOString(),
        }

        saveWallet(newWallet)
        return newTransaction
      } catch {
        setError('Failed to process withdrawal')
        throw new Error('Failed to process withdrawal')
      } finally {
        setIsLoading(false)
      }
    },
    [saveWallet],
  )

  // Check if user can afford a purchase
  const canAfford = useCallback(
    (amount: number): boolean => {
      return wallet.balance >= amount
    },
    [wallet.balance],
  )

  // Get recent transactions
  const getRecentTransactions = useCallback(
    (limit: number = 5): WalletTransaction[] => {
      return wallet.transactions.slice(0, limit)
    },
    [wallet.transactions],
  )

  // Get transactions by type
  const getTransactionsByType = useCallback(
    (type: WalletTransaction['type']): WalletTransaction[] => {
      return wallet.transactions.filter((txn) => txn.type === type)
    },
    [wallet.transactions],
  )

  // Get total credits
  const getTotalCredits = useCallback((): number => {
    return wallet.transactions
      .filter(
        (txn) =>
          (txn.type === 'credit' || txn.type === 'promo') &&
          txn.status === 'completed',
      )
      .reduce((sum, txn) => sum + txn.amount, 0)
  }, [wallet.transactions])

  // Get total debits
  const getTotalDebits = useCallback((): number => {
    return wallet.transactions
      .filter(
        (txn) =>
          (txn.type === 'debit' || txn.type === 'withdrawal') &&
          txn.status === 'completed',
      )
      .reduce((sum, txn) => sum + txn.amount, 0)
  }, [wallet.transactions])

  // Initialize wallet on mount and listen for updates
  useEffect(() => {
    loadWallet()

    const handleStorageChange = () => loadWallet()
    window.addEventListener('wallet-updated', handleStorageChange)
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('wallet-updated', handleStorageChange)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [loadWallet])

  return {
    wallet,
    balance: wallet.balance,
    transactions: wallet.transactions,
    isLoading,
    error,
    fetchWallet,
    addCredit,
    debit,
    applyPromoCredit,
    processWithdrawal,
    canAfford,
    getRecentTransactions,
    getTransactionsByType,
    getTotalCredits,
    getTotalDebits,
  }
}
